import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { useFullscreenHost } from '@/lib/fullscreen'
import { cx } from '@/lib/cx'
import menu from './ContextMenu.module.css'
import styles from './Select.module.css'

export interface SelectOption<V extends string | number> {
  value: V
  label: string
  /** Пояснение второй строкой в списке («аккаунт старше 24 часов»); в самом поле его нет. */
  description?: string | undefined
}

interface Props<V extends string | number> {
  value: V
  options: SelectOption<V>[]
  onChange: (value: V) => void
  /** Include the selected value in FormData for forms using this control. */
  name?: string | undefined
  /** Для <label htmlFor> (Field): нажатие на подпись открывает список. */
  id?: string | undefined
  /** Подпись для чтения с экрана, когда видимой нет (фильтр журнала аудита). */
  label?: string | undefined
  disabled?: boolean | undefined
  /** Размер поля: sm — в строке списка (канал вебхука). */
  size?: 'md' | 'sm'
  className?: string | undefined
}

const LIST_MAX = 320

/**
 * Выпадающий список вместо системного <select>: поле как наши поля ввода, список — как меню срока
 * своего статуса (кружок выбора справа, выбранный — закрашен). Системный список рисует ОС, и он
 * выбивался из интерфейса: на маке — светлое меню поверх тёмного, стрелка вплотную к краю поля.
 * Клавиатура как у <select>: стрелки, Home/End, Enter и пробел выбирают, Esc закрывает, буква —
 * переход к пункту на неё.
 */
export function Select<V extends string | number>({ value, options, onChange, name, id, label, disabled = false, size = 'md', className }: Props<V>) {
  const trigger = useRef<HTMLButtonElement>(null)
  const anchor = useRef<{ top: number; left: number } | null>(null)
  const list = useRef<HTMLDivElement>(null)
  const host = useFullscreenHost()
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const typed = useRef({ text: '', at: 0 })
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value))
  const selected = options.find((o) => o.value === value)

  const openList = () => {
    if (disabled || options.length === 0) return
    setActive(selectedIndex)
    setOpen(true)
  }
  const close = (refocus: boolean) => {
    setOpen(false)
    if (refocus) trigger.current?.focus()
  }
  const pick = (i: number) => {
    const o = options[i]
    if (!o) return
    if (o.value !== value) onChange(o.value)
    close(true)
  }

  /*
   * Список под полем, во всю его ширину; снизу не влезает — над полем. Размер известен только после
   * отрисовки: ставим прямо в стиль до показа кадра, без второй отрисовки (как ContextMenu).
   */
  useLayoutEffect(() => {
    if (!open) return
    const r = trigger.current?.getBoundingClientRect()
    const el = list.current
    if (!r || !el) return
    anchor.current = { top: r.top, left: r.left }
    const want = Math.min(el.scrollHeight, LIST_MAX)
    const below = window.innerHeight - r.bottom - 12
    const above = r.top - 12
    const down = below >= want || below >= above
    const maxHeight = Math.max(120, Math.min(LIST_MAX, down ? below : above))
    const height = Math.min(want, maxHeight)
    const width = Math.max(r.width, 180)
    el.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - width - 8))}px`
    el.style.top = `${down ? r.bottom + 6 : r.top - 6 - height}px`
    el.style.width = `${width}px`
    el.style.maxHeight = `${maxHeight}px`
    el.style.visibility = 'visible'
    el.focus({ preventScroll: true })
  }, [open])

  // выбранный пункт — сразу на виду, а стрелками список прокручивается за подсветкой
  useLayoutEffect(() => {
    if (!open) return
    const el = list.current
    const option = el?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    if (!el || !option) return
    // Only scroll the list itself. scrollIntoView can scroll the page on mobile,
    // which triggers the outside-scroll listener and closes the list immediately.
    const top = option.offsetTop
    const bottom = top + option.offsetHeight
    if (top < el.scrollTop) el.scrollTop = top
    else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight
  }, [open, active])

  useEffect(() => {
    if (!open) return
    const shut = (refocus: boolean) => {
      setOpen(false)
      if (refocus) trigger.current?.focus()
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!list.current?.contains(t) && !trigger.current?.contains(t)) shut(false)
    }
    // Esc закрывает только список: окно, в котором он открыт, остаётся
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      shut(true)
    }
    const onScroll = (e: Event) => {
      if (list.current?.contains(e.target as Node)) return
      const r = trigger.current?.getBoundingClientRect()
      const at = anchor.current
      // A focus/click may have scrolled the container before this listener was
      // installed. Ignore that queued event if the anchor is still in position.
      if (!r || !at || Math.abs(r.top - at.top) > 1 || Math.abs(r.left - at.left) > 1) shut(false)
    }
    const onResize = () => shut(false)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey, true)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey, true)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  const onTriggerKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      openList()
    }
  }

  const onListKey = (e: KeyboardEvent) => {
    const last = options.length - 1
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => Math.min(last, i + 1))
        return
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => Math.max(0, i - 1))
        return
      case 'Home':
        e.preventDefault()
        setActive(0)
        return
      case 'End':
        e.preventDefault()
        setActive(last)
        return
      case 'Enter':
      case ' ':
        e.preventDefault()
        pick(active)
        return
      case 'Tab':
        close(false)
        return
    }
    // буква — к первому пункту на неё (несколько подряд — по началу слова), как у <select>
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = e.timeStamp
      const text = (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLocaleLowerCase('ru')
      typed.current = { text, at: now }
      const plain = (s: string) => s.replace(/^[#@\s]+/, '').toLocaleLowerCase('ru')
      const found = options.findIndex((o) => plain(o.label).startsWith(text))
      if (found >= 0) setActive(found)
    }
  }

  const listbox = open ? (
    <div
      ref={list}
      id={listId}
      role="listbox"
      tabIndex={-1}
      aria-label={label}
      aria-activedescendant={`${listId}-${active}`}
      className={cx(menu.menu, styles.list, 'klichat-popover')}
      // до расстановки по месту список невидим, иначе мелькнул бы в левом верхнем углу
      style={{ visibility: 'hidden', left: 0, top: 0, '--popover-origin': 'top center' } as CSSProperties}
      onKeyDown={onListKey}
      onContextMenu={(e) => e.preventDefault()}
    >
      {options.map((o, i) => {
        const on = o.value === value
        return (
          <div
            key={String(o.value)}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={on}
            data-index={i}
            className={cx(styles.option, i === active && styles.active)}
            onMouseEnter={() => setActive(i)}
            // фокус остаётся на списке: иначе он ушёл бы с него до выбора
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(i)}
          >
            <span className={styles.text}>
              <span className={styles.optionLabel}>{o.label}</span>
              {o.description ? <span className={styles.description}>{o.description}</span> : null}
            </span>
            <span className={cx(menu.mark, styles.mark, on && menu.markOn)} aria-hidden="true">{on ? <Check size={12} strokeWidth={3} /> : null}</span>
          </div>
        )
      })}
    </div>
  ) : null

  return (
    <>
      {name ? <input type="hidden" name={name} value={value} disabled={disabled} /> : null}
      <button
        ref={trigger}
        type="button"
        id={id}
        className={cx(styles.trigger, size === 'sm' && styles.small, open && styles.open, className)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? close(true) : openList())}
        onKeyDown={onTriggerKey}
      >
        <span className={styles.value}>{selected?.label ?? ''}</span>
        <ChevronDown size={size === 'sm' ? 14 : 18} className={styles.chevron} aria-hidden="true" />
      </button>
      {listbox ? createPortal(listbox, host ?? document.body) : null}
    </>
  )
}
